import { Box, Button, Divider, Typography } from '@mui/material';
import React, {
	RefObject,
	createRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { addChangesHistory } from 'store/ticket-service/asyncActions';
import {
	getActiveTicket,
	getIsReportLoading,
	getIsTicketNavigationButtonsAvailable,
} from 'store/ticket-service/selector';
import {
	setIsReportLoading,
	setIsTicketUpdate,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import { IDiagram, ISummaryStory } from 'store/ticket-service/types';
import { getChoosedFile } from 'store/user-service/selector';
import { setIsExportModalOpen, triggerTicketSave } from 'store/user-service/userSlice';

import { orderDiagrams } from 'utils/orderDiagrams';

import { getBanners, getBusinessImpact } from 'core/chains/ChatGptRequests';
import { ResponseBanner } from 'core/chains/types';
import { HistoryActionTypes, NamespacesList, Statuses, TicketStages } from 'core/enums/common';

import { Banner } from 'components/Banner';
import { IFormattedNotification } from 'components/TicketSteps/NotificationStep';
import { NotificationBlock } from 'components/TicketSteps/NotificationStep/NotificationBlock';
import { generateUseCaseDescriptionJSX } from 'components/TicketSteps/UsecasesStep/UseCaseTableBuilder';

import { ReactComponent as Export } from 'assets/icons/export.svg';
import { ReactComponent as Stars } from 'assets/icons/stars.svg';
import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

import UserStoriesList from '../GeneralStep/UserStoriesList';
import { UserStoryTests } from '../TestCasesStep/UserStoryTests';
import { DiagramImage } from '../UsecasesStep/DiagramImage/DiagramImage';
import { FullViewDiagramModal } from '../UsecasesStep/FullViewDiagramModal';
import { UserStories } from '../UsecasesStep/styled';

import { WorkInProgressModal } from './WorkInProgressModal';
import { saveUserDataToCookies } from 'utils/cookieUtils';

export default function ReportStep() {
	const { t: useCasesTranslate, i18n } = useTranslation(NamespacesList['useCasesStep']);
	const { t: reportTranslate } = useTranslation(NamespacesList['reportStep']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const translationConfig = useMemo(
		() => ({
			translate: useCasesTranslate,
			instance: i18n,
		}),
		[useCasesTranslate, i18n]
	);

	const dispatch = useAppDispatch();

	const activeTicket = useSelector(getActiveTicket);
	const choosedFile = useSelector(getChoosedFile);
	const isReportLoading = useSelector(getIsReportLoading);
	const isNavigationButtonAvailable = useSelector(getIsTicketNavigationButtonsAvailable);

	const [notifications, setNotifications] = useState<Array<IFormattedNotification>>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [banners, setBanners] = useState<Array<ResponseBanner>>([]);
	const [isBannersError, setIsBannersError] = useState<boolean>(false);
	const [areBannersLoading, setAreBannersLoading] = useState<boolean>(false);
	const [businessImpact, setBusinessImpact] = useState<Array<string>>([]);
	const [isBusinessImpactError, setIsBusinessImpactError] = useState<boolean>(false);
	const [isBusinessImpactLoading, setIsBusinessImpactLoading] = useState<boolean>(false);
	const [isFullViewDiagramModalOpen, setIsFullViewDiagramModalOpen] = useState(false);
	const [modalDiagramObj, setModalDiagramObj] = useState<IDiagram>();

	const summaryRef: RefObject<HTMLAnchorElement> = useRef(null);
	const useCasesRef: Record<number, RefObject<HTMLAnchorElement>> = {};

	const description = useMemo(() => {
		return activeTicket?.description || '';
	}, [activeTicket?.description]);

	const userStories: Array<ISummaryStory> = useMemo(
		() => activeTicket?.ticketSummaries || [],
		[activeTicket?.ticketSummaries]
	);

	useEffect(() => {
		if (activeTicket?.status === Statuses['In Progress']) handleTicketApprove();

		dispatch(setIsReportLoading(true));
		if (!isReportLoading) {
			Promise.all([handleBannersLoad(), handleBusinessImpactLoad()]).then((response) => {
				if (response[0] && response[1]) {
					dispatch(setIsTicketUpdate(true));
					dispatch(triggerTicketSave());
				}

				dispatch(setIsReportLoading(false));
			});
		}
	}, []);

	const handleBannersLoad = () =>
		new Promise((resolve) => {
			if (activeTicket?.bannersJson) {
				const bannersResponse = JSON.parse(activeTicket.bannersJson);

				setBanners(bannersResponse);

				resolve(false);
			} else {
				if (description) {
					loadBanners().then((bannersResponse) => {
						setBanners(bannersResponse);

						if (bannersResponse.length) {
							dispatch(
								updateLocalTicket({
									id: activeTicket?.id,
									bannersJson: JSON.stringify(bannersResponse),
								})
							);

							resolve(true);
						}
					});
				}
			}
		});

	const handleBusinessImpactLoad = () =>
		new Promise((resolve) => {
			if (activeTicket?.businessImpact?.length) {
				const parsedBusinessImpact = JSON.parse(activeTicket.businessImpact);
				setBusinessImpact(parsedBusinessImpact);

				resolve(false);
			} else {
				if (activeTicket?.ticketSummaries) {
					loadBusinessImpact().then((businessImpactResponse) => {
						setBusinessImpact(businessImpactResponse);

						if (businessImpactResponse.length) {
							dispatch(
								updateLocalTicket({
									id: activeTicket?.id,
									businessImpact: JSON.stringify(businessImpactResponse),
								})
							);

							resolve(true);
						}
					});
				}
			}
		});

	useEffect(() => {
		const formattedNotifications = activeTicket?.notifications.map(
			(notification: Record<string, any>) => ({
				title: notification.title,
				description: notification.description,
				permissions: {
					Email: notification.email,
					Push: notification.push,
					SMS: notification.sms,
				},
			})
		);

		if (formattedNotifications) setNotifications(formattedNotifications);
	}, [activeTicket?.id]);

	const getFormattedUserStories = (stories: Array<ISummaryStory>) =>
		stories?.length
			? stories.reduce(
					(accumulator, story, index) =>
						accumulator + `${index + 1}.${commonTranslate('modelsInput.story')}: ${story.data}.`,
					''
			  )
			: '';

	const loadBanners = useCallback(async (): Promise<Array<ResponseBanner>> => {
		setAreBannersLoading(() => true);

		return getBanners({ description, fileName: choosedFile?.name })
			.then((response) => {
				setIsBannersError(false);
				return response;
			})
			.catch(() => {
				setIsBannersError(true);
				return [];
			})
			.finally(() => {
				setAreBannersLoading(() => false);
			});
	}, [choosedFile]);

	const loadBusinessImpact = useCallback(async (): Promise<Array<string>> => {
		setIsBusinessImpactLoading(() => true);

		return getBusinessImpact({
			userStories: getFormattedUserStories(activeTicket?.ticketSummaries || []),
			fileName: choosedFile?.name,
		})
			.then((response) => {
				setIsBusinessImpactError(false);
				return response;
			})
			.catch(() => {
				setIsBusinessImpactError(true);
				return [];
			})
			.finally(() => {
				setIsBusinessImpactLoading(() => false);
			});
	}, [choosedFile, activeTicket?.ticketSummaries]);

	const handleTicketApprove = () => {
		dispatch(
			updateLocalTicket({
				id: activeTicket?.id,
				status: Statuses['Done'],
			})
		);

		dispatch(
			addChangesHistory({
				userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
				ticketId: activeTicket?.id || '',
				ticketStep: TicketStages.general,
				actionType: HistoryActionTypes.ticketApprove,
			})
		);
	};

	const showFullViewDiagram = (diagram: IDiagram) => {
		setIsFullViewDiagramModalOpen(true);
		setModalDiagramObj(diagram);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const showFullViewTable = () => {
		setIsModalOpen(true);
	};

	const handleExportModalOpen = () => {
		dispatch(setIsExportModalOpen(true));
	};
	/**
	 * Handle the click event to open the coder application
	 * Save the user data and token in the cookies
	 * Open the coder application with the ticket id
	 * @param {Object} userData - The user data
	 * @param {string} token - The token
	 * @returns {void}
	 */
	const handleClick = () => {
		const userData = JSON.parse(localStorage.getItem('user') || '{}');
		const token = localStorage.getItem('token') || '';
		try {
			
		   saveUserDataToCookies(userData, token);
			
		   window.open(`${window.location.href.replace('/brd', '')}/coder?ticket=${activeTicket?.id}`, '_blank');
			} catch (error) {
			alert('Erreur lors de l\'encodage des données. Veuillez réessayer.');
		}
	};
	
	// checks whether user stories exist in the ticket and based on that we enable or didable the "export" report button 
	const isExportButtonEnabled = useMemo(() => {
		return Boolean(activeTicket?.ticketSummaries?.length);
		// return false
	  }, [activeTicket?.ticketSummaries]);



	return (
		<Box data-testid={'report-step'}>
			{activeTicket && (
				<Box
					className="rtl-able"
					sx={{
						height: { xs: 'calc(100% - 84px)', sm: 'calc(100% - 61px)' },
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
						overflow: 'auto',
						padding: i18n?.language === 'ar' ? '0 0 0 12px' : '0',
					}}
				>
					<Box
						sx={{
							boxSizing: 'border-box',
							width: '100%',
							display: 'flex',
							gap: '16px',
							paddingRight: '20px',
						}}
					>
						<Box
							sx={{
								width: { xs: '100%', lg: '70%' },
								display: 'flex',
								flexDirection: 'column',
								gap: '12px',
							}}
						>
							<Box>
								<Typography sx={{ fontWeight: '700', marginBottom: '4px', color: titleTextColor }}>
									{reportTranslate('titles.description')}
								</Typography>
								<Typography sx={{ color: regularTextColor }}>{activeTicket.description}</Typography>
							</Box>

							<Box sx={{ marginBottom: '16px' }}>
								<Typography
									sx={{
										fontWeight: '700',
										marginBottom: '16px',
										padding: '0 20px 0 0',
										color: titleTextColor,
									}}
								>
									{reportTranslate('titles.objective')}
								</Typography>
								{isBusinessImpactLoading ? (
									<Box
										sx={{
											width: '100%',
											height: '100%',
											display: 'flex',
											alignItems: 'flex-start',
											justifyContent: 'center',
										}}
									>
										<Oval
											height={24}
											width={24}
											color="#fff"
											wrapperStyle={{}}
											wrapperClass=""
											visible={true}
											ariaLabel="oval-loading"
											secondaryColor="#4786ff"
											strokeWidth={5}
											strokeWidthSecondary={5}
										/>
									</Box>
								) : isBusinessImpactError ? null : (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: '16px',
											padding: i18n?.language === 'ar' ? '0 20px 0 0' : '0',
											width: '98%',
										}}
									>
										{businessImpact.map((item: string, index: number) => (
											<Typography
												key={`businessImpact ${item} ${index}`}
												sx={{ color: regularTextColor }}
											>
												{item}
											</Typography>
										))}
									</Box>
								)}
							</Box>

							<Box>
								{userStories?.length ? (
									<>
										<Typography
											sx={{ fontWeight: '700', marginBottom: '4px', color: titleTextColor }}
										>
											{reportTranslate('titles.userStories')}
										</Typography>
										<UserStories>
											<UserStoriesList ticketSummaries={userStories} />
										</UserStories>
									</>
								) : null}
							</Box>
						</Box>

						<Divider
							sx={{
								display: { xs: 'none', lg: 'block' },
							}}
							orientation="vertical"
							flexItem
						/>

						<Box
							sx={{
								display: { xs: 'none', lg: 'block' },
								width: { xs: '100%', md: '30%' },
							}}
						>
							<Typography
								sx={{
									fontWeight: '700',
									color: titleTextColor,
								}}
							>
								{reportTranslate('banners.bannersTitle')}
							</Typography>
							<Typography
								sx={{
									fontSize: '14px',
									fontWeight: '400',
									marginBottom: '24px',
									color: regularTextColor,
								}}
							>
								{reportTranslate('banners.bannersDescription')}
							</Typography>

							{areBannersLoading ? (
								<Box
									sx={{
										width: '100%',
										height: '100%',
										display: 'flex',
										alignItems: 'flex-start',
										justifyContent: 'center',
									}}
								>
									<Oval
										height={24}
										width={24}
										color="#fff"
										wrapperStyle={{}}
										wrapperClass=""
										visible={true}
										ariaLabel="oval-loading"
										secondaryColor="#4786ff"
										strokeWidth={5}
										strokeWidthSecondary={5}
									/>
								</Box>
							) : isBannersError ? null : (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: '24px',
										height: '100%',
										overflow: 'auto',
									}}
								>
									{banners?.map((banner, index) => (
										<Banner
											key={index}
											bannerData={banner}
											isLast={index === banners.length - 1}
										></Banner>
									))}
								</Box>
							)}
						</Box>
					</Box>

					<Box sx={{ display: 'none' }}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
							}}
							ref={summaryRef}
						>
							<p style={{ fontWeight: '700', marginBottom: '12px', fontSize: '24px' }}>
								{activeTicket?.title}
							</p>

							<div>
								<p style={{ fontWeight: '700', marginBottom: '4px' }}>
									{reportTranslate('titles.description')}
								</p>
								<p>{activeTicket.description}</p>
							</div>

							<div>
								{userStories?.length ? (
									<>
										<Typography
											sx={{
												fontWeight: 700,
												marginBottom: { md: '8px', xl: '20px' },
											}}
										>
											{reportTranslate('titles.userStories')}
										</Typography>
										<UserStories>
											<UserStoriesList ticketSummaries={userStories} />
										</UserStories>
									</>
								) : null}
							</div>
						</Box>
					</Box>

					<Box>
						<p
							style={{
								fontWeight: 700,
								fontSize: '18px',
								padding: '0 20px 0 0',
								color: titleTextColor,
							}}
						>
							{reportTranslate('titles.useCases')}
						</p>

						{activeTicket.useCases &&
							activeTicket.useCases.map((data, index) => {
								useCasesRef[index] = createRef<HTMLAnchorElement>();

								return (
									<Box key={index} ref={useCasesRef[index]}>
										<p
											style={{
												fontWeight: 700,
												margin: '0 20px 20px 0',
												color: regularTextColor,
											}}
										>
											{useCasesTranslate('generalText.useCase')} #{index + 1}: {data.title}
										</p>

										<Box
											sx={{
												display: 'flex',
												justifyContent: 'center',
											}}
										>
											<Box
												sx={{
													width: '100%',
													paddingRight: '20px',
												}}
											>
												{generateUseCaseDescriptionJSX(
													JSON.parse(data.tables[0].table),
													undefined,
													undefined,
													undefined,
													translationConfig
												)}
											</Box>
										</Box>

										{orderDiagrams(data.diagrams).map((diagram, index) => (
											<Box key={index}>
												{diagram?.pictureLink ? (
													<>
														<Typography
															sx={{
																fontWeight: 700,
																margin: '16px 20px 0 0',
															}}
														>
															{useCasesTranslate(`diagrams.${diagram.title}`)}
														</Typography>
														<Box
															sx={{
																position: 'relative',
																display: 'flex',
																justifyContent: 'center',
															}}
														>
															<DiagramImage diagram={diagram} onFullView={showFullViewDiagram} />
														</Box>
													</>
												) : null}
											</Box>
										))}
									</Box>
								);
							})}
					</Box>

					{activeTicket.userStoryTests && (
						<UserStoryTests isAllTextBlack userStoryTests={activeTicket.userStoryTests} />
					)}

					<Box>
						<Typography
							sx={{
								fontWeight: '700',
								marginBottom: '16px',
								padding: '0 20px 0 0',
								color: titleTextColor,
							}}
						>
							{reportTranslate('titles.notifications')}
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '24px',
								padding: i18n?.language === 'ar' ? '0 20px 0 0' : '0',
								boxSizing: 'border-box',
								width: '98%',
							}}
						>
							{notifications.map((notification: IFormattedNotification, index: number) => (
								<NotificationBlock
									key={notification.title}
									title={notification.title}
									description={notification.description}
									permissions={notification.permissions}
									index={index}
									isReport
								/>
							))}
						</Box>
					</Box>

					<Box
						sx={{
							boxSizing: 'border-box',
							padding: i18n?.language === 'ar' ? '0 20px 0 0' : '0',
							display: { xs: 'block', lg: 'none' },
						}}
					>
						<Typography
							sx={{
								fontWeight: '700',
							}}
						>
							{reportTranslate('banners.bannersTitle')}
						</Typography>
						<Typography
							sx={{
								fontSize: '14px',
								fontWeight: '400',
								marginBottom: '24px',
							}}
						>
							{reportTranslate('banners.bannersDescription')}
						</Typography>

						{areBannersLoading ? (
							<Box
								sx={{
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<Oval
									height={24}
									width={24}
									color="#fff"
									wrapperStyle={{}}
									wrapperClass=""
									visible={true}
									ariaLabel="oval-loading"
									secondaryColor="#4786ff"
									strokeWidth={5}
									strokeWidthSecondary={5}
								/>
							</Box>
						) : isBannersError ? null : (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '24px',
									height: '100%',
									overflow: 'auto',
								}}
							>
								{banners?.map((banner, index) => (
									<Banner
										key={index}
										bannerData={banner}
										isLast={index === banners.length - 1}
									></Banner>
								))}
							</Box>
						)}
					</Box>
				</Box>
			)}

			{isModalOpen && <WorkInProgressModal handleCloseModal={handleCloseModal} />}
			{isFullViewDiagramModalOpen && modalDiagramObj && (
				<FullViewDiagramModal handleCloseModal={handleCloseModal} diagram={modalDiagramObj} />
			)}

			<>
				<Divider />

				<Box
					sx={{
						marginTop: '16px',
						display: 'flex',
						justifyContent: 'space-between',
						width: '100%',
						gap: '8px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							gap: '16px',
						}}
					>
						<Button
							sx={{
								height: '44px',
								minWidth: '220px',
								textTransform: 'none',
								padding: '12px 16px',
								fontSize: '16px',
								fontWeight: '700',
								color: '#4786FF',
								gap: '8px',
								'& svg': {
									filter: () => !isExportButtonEnabled ? 'grayscale(100%) opacity(38%)' : 'none'
								}
								// color: isExportButtonEnabled ? '#4786FF' : 'rgba(0, 0, 0, 0.38)', // Default MUI disabled text color
								// '&.Mui-disabled': {
								// color: 'rgba(0, 0, 0, 0.38)',
								// borderColor: 'rgba(0, 0, 0, 0.12)', // Default MUI disabled border color
								// },	
							}}
							variant= {isExportButtonEnabled ? "outlined" : "contained"}
							disabled={!isExportButtonEnabled}
							color="primary"
							onClick={handleExportModalOpen}
						>
							{reportTranslate('buttons.export')}
							<Export />
						</Button>

						{isNavigationButtonAvailable ? (
							<Button
								sx={{
									height: '44px',
									minWidth: '220px',
									textTransform: 'none',
									padding: '12px 16px',
									fontSize: '16px',
									fontWeight: '700',
									gap: '8px',
								}}
								variant="contained"
								color="primary"
								disabled={true}
								onClick={() => showFullViewTable()}
							>
								{reportTranslate('buttons.createApplication')}
								<Stars style={{ marginLeft: '4px', width: '20px' }} />
							</Button>
						) : null}
					</Box>

					{isNavigationButtonAvailable ? (
						<Button
							sx={{
								height: '44px',
								minWidth: '220px',
								textTransform: 'none',
								padding: '12px 16px',
								fontSize: '16px',
								fontWeight: '700',
								gap: '8px',
							}}
							variant="contained"
							color="primary"
							disabled={true}
							onClick={() => showFullViewTable()}
						>
							{reportTranslate('buttons.generateBenchmark')}
							<Stars style={{ marginLeft: '4px', width: '20px' }} />
						</Button>
					) : null}

					{isNavigationButtonAvailable ? (
						<Button
							sx={{
								height: '44px',
								minWidth: '220px',
								textTransform: 'none',
								padding: '12px 16px',
								fontSize: '16px',
								fontWeight: '700',
								gap: '8px',
							}}
							variant="contained"
							color="primary"
							//disabled={true}
							onClick={() => handleClick()}
						>
							{reportTranslate('buttons.developApp')}
							<Stars style={{ marginLeft: '4px', width: '20px' }} />
						</Button>
					) : null}
				</Box>
			</>
		</Box>
	);
}
